(ns app.ui.reports.new-table
  (:require ["fixed-data-table-2" :refer [Table Column Cell]]
            [clojure.set]
            [clojure.zip]
            [helix.core           :as helix]
            [helix.dom            :as d]
            [helix.hooks          :as hooks]
            [ui.helix-hoplon      :as helix-hoplon]
            [ui.util              :as util]
            [hoplon.core          :as h]
            [app.ui.reports.common :as reports-common]
            ["@mui/material"      :refer [Tooltip IconButton Paper]]
            ["@mui/icons-material" :refer [Help]]))


(defn sort-rows [report-data]
  (assoc report-data :rollup-rows
         (sort-by #(get-in % [:rollup "start"]) util/compare-descending
                  (:rollup-rows report-data))))



;; Okay, so the data model for this table is a bit weird here and I
;; want to explain what is going on as best I can. First, we have
;; nested rows, but this table doesn't really support nested rows. It
;; supports some sort of expansion for some extra details, but not a
;; variable number of rows there. So, this is the problem we are
;; trying to solve, how to get it to have nested rows.

;; But there is a further complication, in order to support large
;; reports, we are using a table that has "virtualized" or "windowed"
;; rendering. This means that only thing rendered is what you see (or
;; maybe a few rows above and beneath) and everything offscreen is not
;; rendered until it is scrolled to.

;; In order to support this style of rendering, there are a few
;; constraints. First, we need to know the height of each element row
;; and the total number of rows before hand. This is to ensure we can
;; setup our scroll bars and scroll length properly. If we didn't know
;; the height and number of the rows how would we know how big to make
;; the scrollbar?

;; Second is that the library for rendering the table has to decide
;; when a row renders. We can't just pass it a list of rendered rows,
;; because that defeats the purpose. Instead, it accepts a callback
;; that will be handed and index and using that index, we will render
;; the appropriate row.

;; Okay, so with that out of the way, we need to talk about nested
;; rows. First making a row look "nested" is easy, we just put blanks
;; in for a few columns. But toggling these nested rows is the tricky
;; part. You see, since we must supply a row count, we need to make
;; that row count based on which groups of rows are shown and which
;; aren't. But further, since we are handed an index, we need to
;; project our nested set of rows into a flat set so we can map our
;; index to the proper row, given the state of open and closed rows.


;; There are a few moving parts here. First, we flatten our list into
;; a vector so that row lookups by index will be constant time.

;; Next we have to think about how to represent groups in an abstract
;; way that will let us map our indexes. Let's see how we go from
;; groups to something in clojure.

;; Group A
;;   A1
;;   A2
;;   A3
;; Group B
;;   B1
;;   B2
;; Group C
;;   C1


;; Imagine we have the above groups. We can represent these groups
;; in clojure as the following. Here we aren't concerned with the data
;; in the groups, just with their counts because the data is stored in
;; our flat list.

;; [{:id :A
;;   :child-count 3}
;;  {:id :B
;;   :child-count 2}
;;  {:id :C
;;   :child-count 1}]

;; This is what `generate-groups` does for us. Takes our groups and
;; spits out this format. (In the actual code our id is chosen by
;; `group-selector` and is a vector of start and end date).

;; Next we need to know which groups are open or closed, we can do that by
;; having a set of the shown group ids.

;; #{:A :B}
;; Here :A would be open and :B, but not :C.

;; Finally given this data we have two things we need to do. First we
;; need to know the number of rows to be displayed. A linear loop
;; through the groups could tell us that. If the group is open, we add
;; child-count + 1, otherwise, we just add 1.

;; But we also need to know for any given index what row we need to
;; show. Imagine that group B is th only group shown. We need a
;; function that conceptually is like this.

;; 1 ──────▶ Group A
;; 2 ──────▶ Group B
;; 3 ──────▶   B1
;; 4 ──────▶   B2
;; 5 ──────▶ Group C

;; But what is the index of these various elements in our flat list?

;; 1 ──────▶ 0
;; 2 ──────▶ 4
;; 3 ──────▶ 5
;; 4 ──────▶ 6
;; 5 ──────▶ 7

;; So how do we make such a function? Well, one way is to taken in our
;; index, run linearly through our groups and figure out the right
;; answer. Another is to build up an index. The former is worst-case
;; 0(n * m) where n is the number of rows in our collection and m is the
;; number of rows being rendered. But think about scrolling, that
;; means in order to scroll we need to do O(n * m) for all the rows we
;; scroll past. Doing this was very very slow.

;; Instead we can build up an index, in the case above this is the
;; clojure representation of our index.

;; [0 4 5 6 7]

;; Vectors are really just functions from Nat -> Elem. Where the Nat
;; is the index of the vector. So we have a function from
;; shown-row-index -> flat-row-index. Also, we can find the length of
;; a vector in constant time, so we can know our row count as well.

;; We can build this index by looping over the groups and adding the
;; shown rows indexes. I am not really happy with the code I wrote for
;; this. I have tried a ton of different ways. But the imperative code
;; is actually way clearer in my view.

;; position = 0
;; indexes = []
;; for group in groups:
;;     indexes.append(position)
;;     position += 1
;;     if group["id"] in shown_groups:
;;         for i in range(group["child_count"]):
;;             indexes.append(position)
;;             position += 1;
;;     else:
;;         position += group["child_count"]


;; I tried a ton of different ways to write this, but nothing
;; approached the clarity that this seems to have. So hopefully this
;; is helpful to someone.

;; Now with this index built we are able to easily look up what index
;; in the flat list our shown row belongs to. Making our scrolling
;; nice and smooth. The code to accomplish all of this is mainly the
;; four functions below, which take up less space than this comment,
;; but I wanted it to be clear why they work this way.



(defn sort-promoted-purchased
  "As the data gets scrambled up in compute.cljc and loses its original start, ordinal, promotedproductskuid,
   we lose the correct sorting that maintains the totals/subtotals (promoted/purchased) partitioning achieved in SQL.

   This function takes the flat list of details and re-establishes the same ordering:
   * sorting by 'start' asc,
   * tie-break on 'ordinal' (sum(impressions) of the promoted group on each row) desc,
   * tie-break on 'promotedproductskuid' desc

   This allows us to render the promoted rows above the purchased rows (partition of promoted, i.e. promoted->purchased children)"
  [details]
  (sort-by (juxt #(get % "start")                          ;; Sort windows by start (default asc),
                 (comp - #(get % "ordinal"))               ;; + tie-break 'ordinal' desc - each row in window will have the same sum(impressions) repeated
                 (comp - #(get % "promotedproductskuid"))) ;; + tie break 'promotedproductskuid' desc - selecting any purchased dimensions implicitly adds promotedproductskuid, tie break on that
           compare
           details))

(defn contains-promoted-purchased-products?
  [report-data]
  (seq (clojure.set/intersection (set (get-in report-data [:report-meta "Request" "GroupBy"]))
                                 (clojure.set/union reports-common/purchased-product-group-bys
                                                    reports-common/promoted-product-group-bys))))

(defn flatten-report-data [report-data]
  (->> (:rollup-rows report-data)
       (mapcat
        (fn [row]
          (cons (:rollup row)
                (let [details (map (fn [[_ detail]]
                                     (assoc detail :is-subrow true))
                                   (:details row))]
                   ;; If any of the Promoted / Purchased Product dimensions, then we expect there to be present and sort on (start + ordinal + promotedProductSkuId)
                  (if (contains-promoted-purchased-products? report-data)
                    (sort-promoted-purchased details)
                    details)))))
       (into [])))

(helix/defnc TooltipCellContent [{:keys [tooltip] :as attr}]
  (helix/$
    Paper
    {:elevation 1
     :style     #js {:padding  "1rem"}
                    :fontSize "12px"}
    (if (:link tooltip)
      (d/div
        (helix/$
          helix-hoplon/text-or-cell
          {:text (:title tooltip)})
        (d/br)
        (d/a {:href @(:link tooltip)
              ;; This is important, because closing tooltip not implemented on re-direct
              :target "_blank"}
             (helix/$
               helix-hoplon/text-or-cell
               {:text (:subtext tooltip)})))

      (helix/$
        helix-hoplon/text-or-cell
        {:text (:title tooltip)}))))

(helix/defnc TooltipCell [{:keys [height text tooltip] :as _attr}]
  "A custom 'fixed-data-table-2' Cell component with Material UI Tooltip,
  supports optional {:subtext ... :link ...} subtext with link on :tooltip"
  (let [cell (if tooltip
               (helix/$
                Cell
                (d/div
                 text
                 (helix/$ Tooltip
                          {:title (helix/$ TooltipCellContent {:tooltip tooltip})}
                          (helix/$
                           IconButton
                           {:size "small" :style #js {:cursor "default"
                                                      :padding "1px"}}
                           (helix/$ Help {:fontSize "small"})))))

               (helix/$
                Cell
                (d/div
                 (helix/$
                  helix-hoplon/text-or-cell
                  {:text text}))))]
    (d/div
     {:style {:alignItems "center"
              :display "flex"
              :height height}}
     cell)))

(defn group-selector [row]
  (select-keys row ["start" "end"]))

(defn generate-groups [report-data]
  (mapv (fn [row]
          {:id (group-selector (:rollup row))
           :child-count (count (:details row))})
        (:rollup-rows report-data)))


(defn build-index [groups shown-groups]
  (loop [position 0
         indexes []
         [group & groups] groups]
    (if (not group)
      indexes
      (let [next-position (inc position)
            child-count (:child-count group)
            upper-range (if (shown-groups (:id group))
                          (+ next-position child-count)
                          next-position)]
        (recur (+ next-position child-count)
               (into indexes (range position upper-range))
               groups)))))


(defn toggle-shown-group [row shown-groups]
  (let [group (group-selector row)]
    (if (contains? shown-groups group)
      (disj shown-groups group)
      (conj shown-groups group))))

(defn shown? [row shown-groups]
  (contains? shown-groups (group-selector row)))

(defn expand-arrow [expanded?]
  (if expanded? "▼" "▶"))

;; Stolen from report.cljs so there is no circular dependency

(def link-formats {"campaignid" {:prefix "campaign"
                                 :suffix "flights"}
                   "flightid"   {:prefix "flight"
                                 :suffix "edit"}
                   "advertiserid" {:prefix "advertiser"
                                   :suffix "campaigns"}})

(defn link-formatter [id-field]
  (fn [row field]
    (let [{:keys [prefix suffix]} (get link-formats id-field)]
      (d/a {:href (str "#!/" prefix "/" (get row id-field) "/" suffix "/")}
           (str (get row field))))))

(defn default-value-link-formatter [id-field value-to-replace replacement]
  (fn [row field]
    (let [{:keys [prefix suffix]} (get link-formats id-field)]
      (d/a {:href (str "#!/" prefix "/" (get row id-field) "/" suffix "/")}
           (str (if (= (get row field) value-to-replace) replacement (get row field)))))))

(defn default-value-formatter [id-field value-to-replace replacement]
  (fn [row field]
    (str (if (= (get row field) value-to-replace) replacement (get row field)))))



(defn format-date
  "Converts a date to a string using the specified format, which
  defaults to `YYYY-MM-DDTHH:mm:ss`, the options map exposes
  `read-as-utc?` flag that specifies if the date should be read as UTC
  (default) or should it be read as is, without any TZ conversions."
  ([date]
   (format-date date "YYYY-MM-DDTHH:mm:ss"))
  ([date fmt & [{:keys [read-as-utc?]
                 :or   {read-as-utc? true}}]]
   (if read-as-utc?
     (-> js/moment (.utc date) (.format fmt))
     (-> (js/moment. date) (.format fmt)))))
;; difference between calling moment.utc(date) instead of moment(date).utc()
;; will parse the date as UTC time (what we receive from server),
;; and avoid parsing it as local TimeZone and then converting it UTC, resulting in different DateTime
;; more https://momentjs.com/docs/#/parsing/utc/


(defn format-datetime
  [row field]
  (let [date (get row field)]
    (d/span
     {:class "datetime"}
     (d/span {:class "date"} (str (format-date date "YYYY-MM-DD")))
     (d/div {:class "time"
             :style {:padding-left (if (= field "start") 16 0)}}
            (str (format-date date "hh:mm A"))))))

(def react-formatters
  {"advertisername" {:format (link-formatter "advertiserid")}
   "campaignname" {:format (default-value-link-formatter "campaignid" 0 "<Campaign Name Pending>")}
   "flightname" {:format (default-value-link-formatter "flightid" 0 "<Flight Name Pending>")}
   "creativename" {:format (default-value-formatter "creativename" 0 "<Creative Name Pending>")}
   "adname" {:format (default-value-formatter "adname" 0 "Ad Name Pending>")}
   "start" {:format format-datetime
            :summary-format (constantly "Total")}
   "end" {:format format-datetime
          :summary-format (fn [& _] "")}})

(helix/defnc table-cell [{:keys [index
                                 field
                                 groupings
                                 shown-groups
                                 all-report-rows
                                 report-field-formats-fn
                                 format-field-value
                                 has-groups?
                                 data]}]
  (let [row (hooks/use-memo [all-report-rows index data]
              (get all-report-rows (get index (.-rowIndex data))))
        formatted (hooks/use-memo
                   [row field format-field-value report-field-formats-fn (.-rowIndex data) shown-groups]
                   (format-field-value row field report-field-formats-fn))]

    (helix/$
     Cell (cond (and (#{"start" "end"} field)
                     (:is-subrow row))
                ""
                (and (not (:is-subrow row)) (groupings field))
                ""
                (and (= field "start") has-groups?)
                (d/span
                 (expand-arrow (shown? row shown-groups))
                 " "
                 formatted)
                :else
                formatted))))


(defn determine-column-width [all-report-rows field]
  ;; 7 here is a magic number I got just by looking at the browser and
  ;; seeing what would make it so the row would fit. There should be a
  ;; better way to do this. But building some dom node and measuring
  ;; it seemed like overkill.
  ;; I won't be surprised if this is wrong in some cases, but it gets
  ;; the right approximate width and the worst thing that will happen
  ;; is some text on a new line.
  ;; The real question is do we really need these huge campaign names
  ;; to fully show up?
  (* 7 (reduce (fn [acc x]
                 (max acc (if (number? (get x field)) 0 (count (get x field)))))
               0
               all-report-rows)))


(helix/defnc report-table [{:keys [report-data
                                   report-field-titles
                                   report-field-titles-tooltips
                                   report-field-formats-fn
                                   format-field-value
                                   window-size-cell
                                   graph-height
                                   top-y
                                   summary-width
                                   non-event-fields
                                   show-events?
                                   show-revenue?
                                   show-gmv?
                                   show-ivt?
                                   show-impression-bucketing?]}]

  (let [[shown-groups set-shown-groups] (hooks/use-state #{})
        ;; Not a super big fan of using the hoplon watchers of size
        ;; here, but didn't want to reinvent the wheel and it works
        [[width height]] (helix-hoplon/use-cell window-size-cell)
        [top-y] (helix-hoplon/use-cell top-y)

        ;; Eventually, we just need to make these things not cells,
        ;; but that will take a lot more work.
        [report-data] (helix-hoplon/use-cell report-data)
        [show-events?] (helix-hoplon/use-cell show-events?)
        [show-revenue?] (helix-hoplon/use-cell show-revenue?)
        [show-gmv?] (helix-hoplon/use-cell show-gmv?)
        [show-ivt?] (helix-hoplon/use-cell show-ivt?)
        [non-event-fields] (helix-hoplon/use-cell non-event-fields)
        [report-field-titles] (helix-hoplon/use-cell report-field-titles)
        [report-field-formats-fn] (helix-hoplon/use-cell report-field-formats-fn)
        report-field-formats-fn (hooks/use-callback [report-field-formats-fn]
                                                    (fn []
                                                      (merge (report-field-formats-fn)
                                                             react-formatters)))
        ;; For some reason the web worker doesn't sort the data. Why?
        ;; No idea.
        report-data (hooks/use-memo [report-data]
                                    (if (not (coll? report-data))
                                      []
                                      (sort-rows report-data)))
        ;; Okay so this is a bit weird. We need to flatten the data,
        ;; because the fixed data table actually renders a flat list
        ;; of rows. It hands us a row index and we tell it how to
        ;; render. For the nested, collapsible rows, we are faking
        ;; this and doing math to know how to index in properly.
        all-report-rows (hooks/use-memo [report-data]
                                        (flatten-report-data report-data))
        ;; We we get a list of all the groups containing only an
        ;; identifier and a childcount. We need this to build up our
        ;; index so we know which rows to show based on what is and
        ;; isn't collpased at the moment.
        groups (hooks/use-memo [report-data]
                               (generate-groups report-data))
        ;; This rebuilds our index. Reifying this structure rather
        ;; than computing on the fly is much faster. This is a O(N)
        ;; operation, but it makes so that all row lookups are O(1)
        ;; See massive comment at start of file
        index (hooks/use-memo [groups shown-groups]
                              (build-index groups shown-groups))
        has-groups? (hooks/use-memo [report-data] (not (empty? (:groupings report-data))))
        groupings (hooks/use-memo [report-data]
                                  (clojure.set/difference
                                   (set
                                    (:detail-fields report-data))
                                   (set (:rollup-fields report-data))))

        fields (hooks/use-memo [report-data shown-groups show-events? show-revenue? show-gmv? show-ivt? show-impression-bucketing?]
                               (let [fields (if (empty? shown-groups)
                                              (:rollup-fields report-data)
                                              (concat ["start" "end"] (:detail-fields report-data)))
                                     revenue-exclusion (if show-revenue?  #{} #{"revenue" "ecpm"})
                                     gmv-exclusion (if show-gmv?  #{} #{"gmv" "roas"})
                                     ivt-exclusion (if show-ivt? #{} #{"unfilteredimpressions" "invaliduaimpressions" "testclicks" "suspiciousclicks" "invaliduaclicks" "duplicateimpressionclicks" "duplicateipclicks" "invaliduseragent" "validuseragent" "botfilteringdisabled"})
                                     impression-bucketing-exclusion (if show-impression-bucketing? #{} #{"duplicateimpressions" "suspiciousimpressions" "uniqueimpressions" "rawimpressions"})
                                     event-inclusion (if show-events?
                                                       (clojure.set/union revenue-exclusion
                                                                          gmv-exclusion
                                                                          ivt-exclusion
                                                                          (set fields))
                                                       non-event-fields)]

                                 (->> fields
                                      (remove revenue-exclusion)
                                      (remove gmv-exclusion)
                                      (remove ivt-exclusion)
                                      (remove impression-bucketing-exclusion)
                                      (filter event-inclusion))))
        field-widths (hooks/use-memo [all-report-rows]
                                     {"adtypename" (determine-column-width all-report-rows "adtypename")
                                      "flightname" (determine-column-width all-report-rows "flightname")
                                      "adname" (determine-column-width all-report-rows "adname")
                                      "campaignname" (determine-column-width all-report-rows "campaignname")
                                      "advertisername" (determine-column-width all-report-rows "advertisername")
                        ;; request counts width to render tooltips correctly
                                      "requestcount" 140
                                      "decisionrequestcount" 150})
        header-height 75]

    (helix/$ Table
             {:className "report"
              :height (- height top-y graph-height)
              :width (- width summary-width)
              :rowHeight 50
              :rowClassNameGetter (fn [i] (when (not (:is-subrow (get all-report-rows (get index i))))
                                            "interactive"))
              :onRowClick (fn [e i]
                            (let [row (get all-report-rows (get index i))]
                              (when (and has-groups? (not (:is-subrow row)))
                                (set-shown-groups
                                 (toggle-shown-group row shown-groups)))))
              :rowsCount (count index)
              :headerHeight header-height
              :footerHeight 30}
             (for [field fields]
               (helix/$ Column
                        {:key                 field
                         ;; Need to do better than this. The width
                         ;; isn't wide enough and maybe needs to be
                         ;; based on the actual contents. The
                         ;; FixedDataTable needs a defined width.
                         :width               (get field-widths field 120)
                         :allowCellsRecycling true
                         :header              (helix/$ TooltipCell
                                                       {:height header-height
                                                        :text    (get report-field-titles field field)
                                                        :tooltip (get report-field-titles-tooltips (keyword field))})
                         :flexGrow            1
                         :footer              (helix/$ Cell
                                                       (format-field-value
                                                        (:summary report-data)
                                                        field
                                                        report-field-formats-fn
                                                        {:mode :summary}))

                         :cell                (fn [data]
                                                (helix/$ table-cell {:index                   index
                                                                     :groupings               groupings
                                                                     :field                   field
                                                                     :has-groups?             has-groups?
                                                                     :shown-groups            shown-groups
                                                                     :all-report-rows         all-report-rows
                                                                     :report-field-formats-fn report-field-formats-fn
                                                                     :format-field-value      format-field-value
                                                                     :data                    data}))})))))
